import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  getToBeProcessed,
  getContractDateDetail,
  bulkConfirmServiceDates,
  getCompleted,
} from 'api/requests/contract-processing-requests';
import {
  ToBeProcessedFilters,
  ToBeProcessedItem,
  BulkConfirmServiceDatesRequest,
  CompletedFilters,
  CompletedItem,
} from 'modules/contract-processing/types';
import {
  CONTRACT_PROCESSING__GENERAL_LOADING,
  CONTRACT_PROCESSING__GET_TO_BE_PROCESSED,
  CONTRACT_PROCESSING__GET_CONTRACT_DATE_DETAIL,
  CONTRACT_PROCESSING__SET_DATA,
  CONTRACT_PROCESSING__SET_ERROR,
  CONTRACT_PROCESSING__BULK_CONFIRM,
  CONTRACT_PROCESSING__SET_BULK_CONFIRM_ERRORS,
  CONTRACT_PROCESSING__GET_COMPLETED,
  CONTRACT_PROCESSING__SET_COMPLETED_DATA,
  CONTRACT_PROCESSING__SET_COMPLETED_ERROR,
} from 'store/constants';

export const setContractProcessingLoadingAction = createAction<boolean>(CONTRACT_PROCESSING__GENERAL_LOADING);
export const setContractProcessingDataAction =
  createAction<{ items: ToBeProcessedItem[]; totalItems: number }>(CONTRACT_PROCESSING__SET_DATA);
export const setContractProcessingErrorAction = createAction<boolean>(CONTRACT_PROCESSING__SET_ERROR);
export const setBulkConfirmErrorsAction = createAction<Record<number, string>>(
  CONTRACT_PROCESSING__SET_BULK_CONFIRM_ERRORS,
);

export const setCompletedDataAction = createAction<{ items: CompletedItem[]; totalItems: number }>(
  CONTRACT_PROCESSING__SET_COMPLETED_DATA,
);
export const setCompletedErrorAction = createAction<boolean>(CONTRACT_PROCESSING__SET_COMPLETED_ERROR);

export const getToBeProcessedAction = createAsyncThunk(
  CONTRACT_PROCESSING__GET_TO_BE_PROCESSED,
  async (props: { companyId: number; filters: ToBeProcessedFilters }, thunkApi) => {
    thunkApi.dispatch(setContractProcessingErrorAction(false));
    thunkApi.dispatch(setContractProcessingLoadingAction(true));
    try {
      const response = await getToBeProcessed(props.filters, props.companyId);
      thunkApi.dispatch(
        setContractProcessingDataAction({
          items: response.items,
          totalItems: response.numberOfResults,
        }),
      );
      return response;
    } catch (error) {
      thunkApi.dispatch(setContractProcessingErrorAction(true));
      throw error;
    } finally {
      thunkApi.dispatch(setContractProcessingLoadingAction(false));
    }
  },
);

export const getCompletedAction = createAsyncThunk(
  CONTRACT_PROCESSING__GET_COMPLETED,
  async (props: { companyId: number; filters: CompletedFilters }, thunkApi) => {
    thunkApi.dispatch(setCompletedErrorAction(false));
    thunkApi.dispatch(setContractProcessingLoadingAction(true));
    try {
      const response = await getCompleted(props.filters, props.companyId);
      if (thunkApi.signal.aborted) {
        return response;
      }
      thunkApi.dispatch(
        setCompletedDataAction({
          items: response.items,
          totalItems: response.numberOfResults,
        }),
      );
      return response;
    } catch (error) {
      if (!thunkApi.signal.aborted) {
        thunkApi.dispatch(setCompletedDataAction({ items: [], totalItems: 0 }));
        thunkApi.dispatch(setCompletedErrorAction(true));
      }
      throw error;
    } finally {
      thunkApi.dispatch(setContractProcessingLoadingAction(false));
    }
  },
);

export const getContractDateDetailAction = createAsyncThunk(
  CONTRACT_PROCESSING__GET_CONTRACT_DATE_DETAIL,
  async (props: { contractDateId: number; companyId: number; serviceDateId?: number | null }, thunkApi) => {
    thunkApi.dispatch(setContractProcessingLoadingAction(true));
    try {
      const response = await getContractDateDetail(props.contractDateId, props.companyId, props.serviceDateId);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return thunkApi.rejectWithValue(403);
      }
      throw error;
    } finally {
      thunkApi.dispatch(setContractProcessingLoadingAction(false));
    }
  },
);

export const bulkConfirmServiceDatesAction = createAsyncThunk(
  CONTRACT_PROCESSING__BULK_CONFIRM,
  async (props: { request: BulkConfirmServiceDatesRequest; companyId: number }, thunkApi) => {
    thunkApi.dispatch(setContractProcessingLoadingAction(true));
    try {
      const response = await bulkConfirmServiceDates(props.request, props.companyId);
      const errorMap: Record<number, string> = {};
      response.forEach(item => {
        errorMap[item.contractDateId] = item.translationKey;
      });
      thunkApi.dispatch(setBulkConfirmErrorsAction(errorMap));
      return response;
    } finally {
      thunkApi.dispatch(setContractProcessingLoadingAction(false));
    }
  },
);
